// Mapping an API 400 onto what a form can actually render.
//
// The API's errorHandler sends zod's `flatten()` as `details`, which is
// `{ formErrors: string[], fieldErrors: Record<string, string[]> }`. Two things
// about that shape have bitten us:
//
//   1. `fieldErrors` is ALWAYS an object. It is `{}` when every issue is
//      root-level — a `.strict()` schema rejecting an unrecognized key, or a
//      superRefine that adds an issue with no `path`. So `if (fieldErrors)` is
//      not a check; it is always true. A form that trusted it showed "Please
//      fix the highlighted fields." with nothing highlighted, and the real
//      reason ("Unrecognized key(s) in object: 'kind'") was thrown away.
//   2. A form only has an inline error slot for the fields it renders. The API
//      can reject a field the form composes rather than shows (an address is
//      five inputs collapsed into one `address_line1`), and that message has
//      nowhere to land.
//
// `parseValidationError` handles both: caller declares which keys it can
// highlight, everything else is folded into the banner text verbatim.

import { ApiError } from '../api/client';

interface ZodFlattened {
  formErrors?: string[];
  fieldErrors?: Record<string, string[] | undefined>;
}

export interface ValidationFailure {
  /** Field key → first message, for the inline `<span className="field-error">`. */
  fieldErrors: Record<string, string>;
  /** Banner text. Only claims fields are highlighted when some actually are. */
  message: string;
}

/**
 * Turn a rejected request into `{ fieldErrors, message }`, or null when it
 * isn't a 400 carrying zod details — callers fall through to their own generic
 * copy ("Could not save the vendor…") in that case, which is the right message
 * for a 500 or a dropped connection.
 *
 * `known` is the set of keys the caller renders inline. Anything outside it,
 * plus every form-level error, goes into `message` so the banner always points
 * at something real.
 */
export function parseValidationError(
  err: unknown,
  known: readonly string[],
): ValidationFailure | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const details = (err.body as { details?: ZodFlattened } | null)?.details;
  if (!details) return null;

  const fieldErrors: Record<string, string> = {};
  const orphans: string[] = [];
  for (const [key, msgs] of Object.entries(details.fieldErrors ?? {})) {
    const first = Array.isArray(msgs) ? msgs[0] : undefined;
    if (first === undefined || first.trim().length === 0) continue;
    if (known.includes(key)) fieldErrors[key] = first;
    // No inline slot for this one — name the field so the banner is actionable.
    else orphans.push(`${key}: ${first}`);
  }

  const loose = [...(details.formErrors ?? []), ...orphans]
    .filter((m) => m.trim().length > 0);
  const highlighted = Object.keys(fieldErrors).length > 0;

  let message: string;
  if (highlighted) {
    message = loose.length > 0
      ? `Please fix the highlighted fields. ${loose.join(' ')}`
      : 'Please fix the highlighted fields.';
  } else {
    // `err.message` is the API's top-level "Invalid request" — vague, but
    // still better than pointing at highlights that don't exist.
    message = loose.length > 0 ? loose.join(' ') : err.message;
  }

  return { fieldErrors, message };
}

/**
 * What to show when a request failed and there is no inline slot to put it in —
 * a page-level banner, a card's error line, a toast.
 *
 * `fallback` is for the cases the server didn't explain: a 500, a dropped
 * connection, anything that isn't an ApiError. Everything the API *did* say is
 * shown, because a generic "Try again." for a failure that will never succeed on
 * retry sends the reader to DevTools. Real examples from this codebase:
 * "Client has no email on file" (400) and "Email delivery failed: …" (502) both
 * rendered as "Could not send invoice. Try again."
 *
 * Keep a hand-written message ahead of this ONLY where it beats the API's, and
 * where the status pins the meaning — a 409 on send is "someone else already
 * sent it", which is worth saying better than the server does.
 */
export function apiErrorText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;

  // Zod details name the offending field, so prefer them when they exist.
  // parseValidationError returns `err.message` verbatim when `details` carried
  // nothing usable — a 400 whose details are a plain hint object, like the send
  // route's `{ to: 'recipient missing…' }` — and that equality is the tell.
  const failure = parseValidationError(err, []);
  if (failure && failure.message.trim() !== '' && failure.message !== err.message) {
    return failure.message;
  }

  // The body's `error`, NOT err.message: api/client.ts synthesizes the latter
  // as "POST /api/invoices/7/send 500" when the body carries no `error`, and
  // that is not a sentence to show anyone.
  const body = err.body as { error?: string } | null;
  const text = (body?.error ?? '').trim();
  return text !== '' ? text : fallback;
}
